import { useEffect, useMemo, useRef } from 'react';

import { FileMimeTypeEnum } from '../../../../../../lib/enums';
import { createBase64DataUrl } from '../../../../../../lib/util/DataHelpers';

import CompanyLogo from '../../../../../../components/CompanyLogo/CompanyLogo';
import { useIsMobile } from '../../../../../../hooks';
import {
  symbolDetailsScrollProgress,
} from '../../../../../../shared_components/utils/scroll/symbolDetailsScrollProgress';
import { logoSources } from '../../../../../../util/customLogos';

import styles from './Logo.module.scss';

const UNSET_SCROLL_PROGRESS = -1;
/** 1.0 -> 0.8, matching the header's own fade. */
const MAX_SCALE_REDUCTION = 0.2;

type Props = {
  symbol: string;
  logo?: string;
  url?: string;
  animateOnScroll?: boolean;
}

const Logo = ({ symbol, logo, url, animateOnScroll = false }: Props) => {
  const { isMobile } = useIsMobile();
  const logoRef = useRef<HTMLDivElement>(null);
  const rafId = useRef<number | null>(null);

  useEffect(() => {
    if (!isMobile || !animateOnScroll) return undefined;
    let prevProgress = UNSET_SCROLL_PROGRESS;

    const tick = () => {
      const progress = symbolDetailsScrollProgress.current;
      if (progress !== prevProgress) {
        prevProgress = progress;
        if (logoRef.current) {
          logoRef.current.style.transform = `scale(${1 - progress * MAX_SCALE_REDUCTION})`;
        }
      }
      rafId.current = requestAnimationFrame(tick);
    };

    rafId.current = requestAnimationFrame(tick);

    return () => {
      if (rafId.current !== null) {
        cancelAnimationFrame(rafId.current);
        rafId.current = null;
      }
    };
  }, [isMobile, animateOnScroll]);

  const theLogo = useMemo(() => createBase64DataUrl(logo!, FileMimeTypeEnum.Png), [logo]);
  const customLogoSource = symbol ? logoSources[symbol] : null;
  return (
    <>
    <div ref={logoRef} className={isMobile && animateOnScroll ? styles.logoScaleWrapper : undefined}>
      {(customLogoSource || url || theLogo) ? (
        <CompanyLogo
          url={url}
          logo={logo}
          symbol={symbol}
          className={styles.logoStyle}
        />
      ) : (
        <div className={styles.logoLoader} />
      )}
    </div>
    </>
  );
};

export default Logo;
